/**
 * useVersionCheck.js
 *
 * Custom hook for checking version updates.
 * Automatically checks for new versions and manages modal visibility.
 */
import { useState, useEffect, useCallback } from 'react';
import { versionAPI } from '../utils/httpClient';

/**
 * Hook for checking version updates
 * @param {boolean} autoCheck - Whether to automatically check on mount (default: true)
 * @param {number} delay - Delay in ms before checking (default: 1000)
 * @returns {Object} - { hasUpdate, versionData, loading, showModal, checkUpdate, closeModal, openModal }
 */
export const useVersionCheck = (autoCheck = true, delay = 1000) => {
    const [hasUpdate, setHasUpdate] = useState(false);
    const [versionData, setVersionData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [checked, setChecked] = useState(false);

    // Check for version updates
    const checkUpdate = useCallback(async () => {
        // Only check if user is authenticated
        const token = localStorage.getItem('authToken') || localStorage.getItem('token');
        if (!token) {
            return;
        }

        // Prevent duplicate checks in the same session
        if (checked) {
            return;
        }

        setLoading(true);
        try {
            const response = await versionAPI.checkUpdate();
            if (response.success && response.data?.has_update) {
                setHasUpdate(true);
                setVersionData(response.data.version);
                setShowModal(true);
            } else {
                setHasUpdate(false);
                setVersionData(null);
            }
            setChecked(true);
        } catch (err) {
            console.error('[useVersionCheck] Version check failed:', err);
            setChecked(true);
        } finally {
            setLoading(false);
        }
    }, [checked]);

    // Auto-check on mount if enabled
    useEffect(() => {
        if (autoCheck && !checked) {
            // Delay check to avoid blocking initial render
            const timer = setTimeout(checkUpdate, delay);
            return () => clearTimeout(timer);
        }
    }, [autoCheck, delay, checkUpdate, checked]);

    // Close modal
    const closeModal = useCallback(() => {
        setShowModal(false);
    }, []);

    // Open modal (for manual trigger)
    const openModal = useCallback(() => {
        if (versionData) {
            setShowModal(true);
        }
    }, [versionData]);

    // Handle version acknowledged
    const handleAcknowledge = useCallback((version) => {
        setHasUpdate(false);
    }, []);

    return {
        hasUpdate,
        versionData,
        loading,
        showModal,
        checkUpdate,
        closeModal,
        openModal,
        handleAcknowledge
    };
};

export default useVersionCheck;
